import { useMemo, useState } from 'react';
import { individuallyConfirmedFields, projectSuggestionGroups } from '@command-center/admin-ecorp';
import { Badge, Button, cn } from '@command-center/ui-system';
import { AlertTriangle, Check, Pencil, ShieldCheck, X } from 'lucide-react';
import type { ExtractionComponent, ExtractionMeta, FieldSuggestion, SuggestionDecision } from '../api';
import { ConfidencePill, display, EmptyState, SectionHeader } from '../lib';

export type DecisionState = Record<string, { action: SuggestionDecision['action']; value?: unknown }>;

export function decisionsFromState(state: DecisionState): SuggestionDecision[] {
  return Object.entries(state).filter(([, entry]) => entry.action !== 'pending').map(([id, entry]) => ({ id, action: entry.action, value: entry.value }));
}

/** Client-side mirror of the Worker's safe-bulk policy, used to preselect rows. */
export function safeDecisionIds(suggestions: FieldSuggestion[]): string[] {
  return suggestions.filter(row => row.review_status === 'pending' && !row.conflict && !row.inferred && (row.confidence ?? 0) >= 0.8 && (row.source_page !== null || row.source_sheet !== null || (row.source_citation ?? '').length > 0) && !individuallyConfirmedFields.has(row.field_key)).map(row => row.id);
}

export function sourceLabel(row: Pick<FieldSuggestion, 'source_page' | 'source_sheet' | 'source_citation'>): string {
  const parts: string[] = [];
  if (row.source_sheet) parts.push(`Sheet ${row.source_sheet}`);
  if (row.source_page !== null && row.source_page !== undefined) parts.push(`p. ${row.source_page}`);
  if (row.source_citation) parts.push(row.source_citation);
  return parts.join(' · ');
}

export function ExtractionSummaryCard({ normalized }: { normalized: ExtractionMeta | null }) {
  if (!normalized) return null;
  return (
    <div className="space-y-3 rounded-xl border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <h4 className="font-medium">Extraction summary</h4>
        <ConfidencePill value={normalized.confidence}/>
      </div>
      {normalized.extractionSummary && <p className="text-sm text-muted-foreground">{normalized.extractionSummary}</p>}
      {normalized.missingInformation.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">Missing information</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">{normalized.missingInformation.map((item, index) => <li key={index}>{item}</li>)}</ul>
        </div>
      )}
      {(normalized.conflicts.length > 0 || normalized.warnings.length > 0) && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">Conflicts & warnings</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">{[...normalized.conflicts, ...normalized.warnings].map((item, index) => <li key={index}>{item}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

const componentLabels: Record<string, string> = {
  building: 'Buildings', room: 'Rooms', tank: 'Tanks', dispenser: 'Dispensers', loadingDock: 'Loading docks',
  accessPoint: 'Access points', parking: 'Parking', evInfrastructure: 'EV infrastructure', equipment: 'Equipment',
  stationarySource: 'Stationary sources', developmentComponent: 'Development components', sensitiveReceptor: 'Sensitive receptors',
  parcel: 'Parcels', queuing: 'Queuing', other: 'Other',
};

export function ComponentCards({ components }: { components: ExtractionComponent[] }) {
  const grouped = useMemo(() => {
    const map = new Map<string, ExtractionComponent[]>();
    for (const component of components) { const list = map.get(component.category) ?? []; list.push(component); map.set(component.category, list); }
    return [...map.entries()];
  }, [components]);
  if (!components.length) return null;
  return (
    <div>
      <SectionHeader title="Development components"/>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {grouped.map(([category, list]) => (
          <div key={category} className="rounded-xl border bg-card p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{componentLabels[category] ?? category} <span className="ml-1 rounded-full bg-muted px-1.5 text-[11px] tabular-nums">{list.length}</span></p>
            <ul className="space-y-1.5">
              {list.map((component, index) => (
                <li key={index} className="text-sm">
                  <span className="font-medium">{component.name}</span>
                  {component.quantity !== null && <span className="ml-1 text-muted-foreground">× {component.quantity}</span>}
                  {component.details && <span className="block text-xs text-muted-foreground">{component.details}</span>}
                  {(component.sourceSheet || component.sourcePage !== null) && <span className="block text-[11px] text-muted-foreground/70">{sourceLabel({ source_page: component.sourcePage, source_sheet: component.sourceSheet, source_citation: null })}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ExtractionReview({ suggestions, state, onState, busy, footer }: {
  suggestions: FieldSuggestion[];
  state: DecisionState;
  onState(next: DecisionState): void;
  busy?: boolean;
  footer?: React.ReactNode;
}) {
  const [editingId, setEditingId] = useState<string>();
  const pending = suggestions.filter(row => row.review_status === 'pending');
  const groups = useMemo(() => {
    const known = new Set(projectSuggestionGroups.flatMap(group => group.fields));
    const list = projectSuggestionGroups.map(group => ({ ...group, rows: pending.filter(row => group.fields.includes(row.field_key)) }));
    const other = pending.filter(row => !known.has(row.field_key));
    return [...list, { id: 'other', label: 'Other', fields: [], rows: other }].filter(group => group.rows.length);
  }, [pending]);

  if (!pending.length) return <EmptyState title="No suggestions awaiting review." hint="Run extraction on a document to generate field suggestions."/>;

  const set = (id: string, action: SuggestionDecision['action'], value?: unknown) => onState({ ...state, [id]: { action, value } });

  return (
    <div className="space-y-5">
      {groups.map(group => (
        <section key={group.id}>
          <SectionHeader title={group.label}/>
          <div className="space-y-2">
            {group.rows.map(row => {
              const decision = state[row.id] ?? { action: 'pending' as const };
              const editing = editingId === row.id;
              return (
                <div key={row.id} className={cn('rounded-xl border bg-card p-3 transition', decision.action === 'accept' || decision.action === 'edit' ? 'border-emerald-500/50' : decision.action === 'reject' ? 'border-red-500/40 opacity-70' : row.conflict ? 'border-amber-500/50' : '')}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{row.field_key}</span>
                    <ConfidencePill value={row.confidence}/>
                    {row.inferred && <Badge variant="secondary">Inferred</Badge>}
                    {row.conflict && <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400"><AlertTriangle className="h-3 w-3"/>Conflicts with current value</span>}
                    {individuallyConfirmedFields.has(row.field_key) && <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground"><ShieldCheck className="h-3 w-3"/>Confirm individually</span>}
                    <span className="ml-auto text-[11px] text-muted-foreground">{sourceLabel(row) || 'No source reference'}</span>
                  </div>
                  <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                    <div className="rounded-lg bg-muted/50 p-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Current</p>
                      <p className="mt-0.5 break-words">{display(row.existing_value) || <span className="text-muted-foreground">Empty</span>}</p>
                    </div>
                    <div className="rounded-lg bg-primary/5 p-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Suggested</p>
                      {editing
                        ? <textarea autoFocus className="field-control mt-1 min-h-16 w-full text-sm" defaultValue={display(decision.value ?? row.extracted_value)} onBlur={e => { set(row.id, 'edit', e.target.value); setEditingId(undefined); }}/>
                        : <p className="mt-0.5 break-words">{display(decision.action === 'edit' ? decision.value : row.extracted_value)}</p>}
                      {row.notes && <p className="mt-1 text-xs text-muted-foreground">{row.notes}</p>}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Button size="sm" variant={decision.action === 'accept' ? 'default' : 'outline'} disabled={busy} onClick={() => set(row.id, decision.action === 'accept' ? 'pending' : 'accept')}><Check className="mr-1 h-3.5 w-3.5"/>Accept</Button>
                    <Button size="sm" variant={decision.action === 'edit' ? 'default' : 'outline'} disabled={busy} onClick={() => { setEditingId(row.id); if (decision.action !== 'edit') set(row.id, 'edit', row.extracted_value); }}><Pencil className="mr-1 h-3.5 w-3.5"/>Edit & accept</Button>
                    <Button size="sm" variant={decision.action === 'reject' ? 'default' : 'outline'} disabled={busy} onClick={() => set(row.id, decision.action === 'reject' ? 'pending' : 'reject')}><X className="mr-1 h-3.5 w-3.5"/>Reject</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
      {footer}
    </div>
  );
}
